import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { fetchPageCopy } from '../lib/copy';
import { useIsMobile } from '../hooks/useIsMobile';
import { usePageTitle } from '../hooks/usePageTitle';

function vimeoEmbedUrl(url) {
  if (!url) return null;
  const match = url.match(/vimeo\.com\/(\d+)/);
  if (!match) return null;
  return `https://player.vimeo.com/video/${match[1]}?autoplay=1&color=1A1814&title=0&byline=0&portrait=0&end_card=0`;
}

function VideoSection({ label, items, vertical, isMobile }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = items[activeIndex] || items[0];

  return (
    <section style={{ padding: isMobile ? '3rem 1.5rem 4rem' : '5rem 3rem 6rem', borderBottom: '1px solid var(--rule)' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        <p style={{ fontSize: '11px', fontWeight: 300, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: isMobile ? '2.5rem' : '3.5rem' }}>
          {label}
        </p>

        {/* Video player */}
        <div style={{
          display: 'flex',
          justifyContent: vertical ? 'flex-start' : 'stretch',
          marginBottom: isMobile ? '2rem' : '3rem',
        }}>
          <div style={{
            ...(vertical
              ? { width: isMobile ? '60%' : '340px', aspectRatio: '9/16' }
              : { width: '100%', aspectRatio: '16/9' }),
            background: 'var(--ink)',
            position: 'relative', overflow: 'hidden', flexShrink: 0,
          }}>
            {active && vimeoEmbedUrl(active.vimeo_url) ? (
              <iframe
                key={active.id}
                src={vimeoEmbedUrl(active.vimeo_url)}
                width="100%" height="100%"
                style={{ position: 'absolute', inset: 0, border: 'none' }}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={active.title}
              />
            ) : (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
                <span style={{ fontSize: isMobile ? '16px' : '22px', color: 'rgba(255,255,255,0.5)' }}>
                  {active?.title || label}
                </span>
                <span style={{ fontSize: '11px', fontWeight: 300, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.08em' }}>
                  VIDEO COMING SOON
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Active project detail */}
        {active && (active.description || active.responsibilities) && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? '1rem' : '3rem',
            marginBottom: '2.5rem', paddingBottom: '2.5rem',
            borderBottom: '1px solid var(--rule)',
          }}>
            {active.description && (
              <p style={{ fontSize: '13px', fontWeight: 300, color: 'var(--ink-mid)', lineHeight: 1.8 }}>
                {active.description}
              </p>
            )}
            {active.responsibilities && (
              <p style={{ fontSize: '13px', fontWeight: 300, color: 'var(--ink-faint)', lineHeight: 1.8 }}>
                {active.responsibilities}
              </p>
            )}
          </div>
        )}

        {/* Project list */}
        <div>
          {items.map((item, i) => (
            <button
              key={item.id || i}
              onClick={() => setActiveIndex(i)}
              style={{
                width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: isMobile ? '0.9rem 0' : '1rem 0',
                borderBottom: '1px solid var(--rule)', textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{
                  fontSize: '10px', fontWeight: 300,
                  color: i === activeIndex ? 'var(--ink)' : 'var(--ink-faint)',
                  width: '14px', textAlign: 'center',
                }}>
                  {i === activeIndex ? '▶' : '○'}
                </span>
                <span style={{
                  fontSize: isMobile ? '18px' : '22px',
                  color: i === activeIndex ? 'var(--ink)' : 'var(--ink-mid)',
                  transition: 'color 150ms',
                }}>
                  {item.title}
                </span>
              </div>
            </button>
          ))}
        </div>

      </div>
    </section>
  );
}

export default function Work() {
  const [videos, setVideos] = useState([]);
  const [copy, setCopy]     = useState({ label_commercial: 'Commercial Work', label_social: 'Social First' });
  const isMobile            = useIsMobile();
  usePageTitle('Work');

  useEffect(() => {
    async function load() {
      const { data: vids } = await supabase.from('portfolio_videos').select('*').order('display_order');
      setVideos(vids || []);
      fetchPageCopy('work').then(c => {
        if (Object.keys(c).length > 0) setCopy(prev => ({ ...prev, ...c }));
      });
    }
    load();
  }, []);

  const commercial = videos.filter(v => v.category !== 'social');
  const social     = videos.filter(v => v.category === 'social');

  const showCommercial = commercial.length > 0 ? commercial : placeholderCommercial;
  const showSocial     = social.length > 0 ? social : null;

  return (
    <div className="page-fade" style={{ paddingTop: '56px' }}>
      <VideoSection label={copy.label_commercial} items={showCommercial} vertical={false} isMobile={isMobile} />
      {showSocial && (
        <VideoSection label={copy.label_social} items={showSocial} vertical={true} isMobile={isMobile} />
      )}
    </div>
  );
}

const placeholderCommercial = [
  { id: 1, title: 'Showreel 2024',     description: 'Selected commercial work.',          responsibilities: 'Director, Editor' },
  { id: 2, title: 'Brand Film',        description: 'Campaign film for a leading brand.', responsibilities: 'Creative Director, Editor' },
  { id: 3, title: 'Product Launch',    description: 'TV and digital launch content.',     responsibilities: 'Producer, Editor' },
  { id: 4, title: 'Documentary Short', description: 'Short documentary commission.',      responsibilities: 'Director, Producer' },
];
